/**
 * One-shot hardware profiling for adaptive UI performance (tiering).
 */

/**
 * @typedef {Object} HardwareStatus
 * @property {boolean} isLowEnd
 * @property {string} reason Human-readable diagnostic when {@link HardwareStatus.isLowEnd} is true; otherwise `"ok"`.
 */

let hardwareStatusPromise;

/**
 * Profiles device capability once per app lifetime.
 *
 * Signals: `navigator.deviceMemory` ≤4 GiB (when exposed),
 * WebGL with `failIfMajorPerformanceCaveat` (falls back when likely software rendering or unsupported).
 * `navigator.hardwareConcurrency` is intentionally not used: it often reflects cgroup/sandbox caps on Linux, not total CPU.
 *
 * @returns {Promise<HardwareStatus>}
 */
export function getHardwareStatus() {
  return (hardwareStatusPromise ??= Promise.resolve(computeHardwareStatus()));
}

function computeHardwareStatus() {
  const reasons = [];

  if (typeof navigator !== "undefined") {
    const dm = navigator.deviceMemory;
    if (typeof dm === "number" && dm <= 4) reasons.push(`deviceMemory<=${dm}GiB`);
  }

  if (typeof document !== "undefined") {
    try {
      const canvas = document.createElement("canvas");
      const gl =
        canvas.getContext("webgl", { failIfMajorPerformanceCaveat: true }) ??
        canvas.getContext("experimental-webgl", { failIfMajorPerformanceCaveat: true });
      if (!gl) reasons.push("webgl-unavailable-or-major-performance-caveat");
    } catch {
      reasons.push("webgl-error");
    }
  }

  return {
    isLowEnd: reasons.length > 0,
    reason: reasons.join("; ") || "ok",
  };
}
